import { useCallback, useState } from "react"

import {
  transport,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"
import { getUserFacingErrorMessage, toast } from "@coval/ui"
import { customAlphabet } from "nanoid"

import {
  WorkspaceCreateForm,
  type WorkspaceCreateDraft,
} from "../components/WorkspaceCreateForm"

const WORKSPACE_SLUG_MAX_LENGTH = 100
const WORKSPACE_SLUG_SUFFIX_LENGTH = 10
const createWorkspaceSlugSuffix = customAlphabet(
  "0123456789abcdefghijklmnopqrstuvwxyz",
  WORKSPACE_SLUG_SUFFIX_LENGTH,
)

function createInternalWorkspaceSlug(displayName: string): string {
  const maxBaseLength =
    WORKSPACE_SLUG_MAX_LENGTH - WORKSPACE_SLUG_SUFFIX_LENGTH - 1
  const base =
    displayName
      .normalize("NFKD")
      .toLowerCase()
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, maxBaseLength)
      .replace(/-+$/g, "") || "workspace"

  return `${base}-${createWorkspaceSlugSuffix()}`
}

interface WorkspaceCreateRouteProps {
  onCancel: () => void
  onCreated: () => void
}

/** Creates a workspace, owning the mutation and its user-facing failures. */
export function WorkspaceCreateRoute({
  onCancel,
  onCreated,
}: WorkspaceCreateRouteProps) {
  const queryUtils = useTransportQueryUtils()
  const createMutation = useTransportMutation(
    transport.operations.createWorkspace,
  )
  const [error, setError] = useState("")

  const handleSubmit = useCallback(
    (draft: WorkspaceCreateDraft) => {
      void createMutation
        .mutateAsync({
          slug: createInternalWorkspaceSlug(draft.displayName),
          display_name: draft.displayName,
        })
        .then(async () => {
          await queryUtils.invalidateOperation(
            transport.operations.listWorkspaces,
          )
          toast.success({ title: "Workspace created" })
          onCreated()
        })
        .catch((mutationError: unknown) => {
          const errorMessage = getUserFacingErrorMessage(mutationError, {
            detailMessage: true,
            fallback: "Could not create the workspace.",
          })
          setError(errorMessage)
          toast.error({
            title: "Could not create workspace",
            description: errorMessage,
            error: mutationError,
          })
        })
    },
    [createMutation, onCreated, queryUtils],
  )

  return (
    <WorkspaceCreateForm
      error={error}
      isPending={createMutation.isPending}
      onCancel={onCancel}
      onDirty={() => setError("")}
      onSubmit={handleSubmit}
    />
  )
}
